import { cloneDeep, isEqual } from "lodash";
import { getCaretPosition } from "../functions/getCaretPosition";
import { readV3Html } from "../functions/readV3Html";
import { setCaretPosition } from "../functions/setCaretPosition";
import { stringToV3 } from "../functions/stringToV3";
import { EditorV3Line } from "./EditorV3Line";
import { EditorV3PositionClass } from "./EditorV3Position";
import { defaultContentProps } from "./defaultContentProps";
import { IMarkdownSettings } from "./defaultMarkdownSettings";
import {
  EditorV3Align,
  EditorV3BlockClass,
  EditorV3ContentProps,
  EditorV3ContentPropsInput,
  EditorV3DropListItem,
  EditorV3Position,
  EditorV3PositionF,
  EditorV3RenderProps,
  EditorV3Styles,
  IEditorV3,
  IEditorV3Line,
} from "./interface";
import { textBlockFactory } from "./textBlockFactory";

/**
 * Represents the content of an EditorV3 instance.
 */
export class EditorV3Content implements IEditorV3 {
  private _defaultContentProps = cloneDeep(defaultContentProps);
  // Standard attributes
  private _textAlignment: EditorV3Align = this._defaultContentProps.textAlignment;
  /**
   * Current text alignment.
   */
  get textAlignment() {
    return this._textAlignment;
  }
  /**
   * Set text alignment.
   *
   * @param newTa - The new text alignment value
   */
  set textAlignment(newTa: EditorV3Align) {
    /* istanbul ignore else */
    if (newTa !== this._textAlignment) {
      this.lines.forEach((l) => (l.contentProps.textAlignment = newTa));
      this._textAlignment = newTa;
    }
  }

  private _decimalAlignPercent = this._defaultContentProps.decimalAlignPercent;
  /**
   * Current decimal alignment percent.
   */
  get decimalAlignPercent() {
    return this._decimalAlignPercent;
  }
  /**
   * Set decimal alignment percent.
   *
   * @param newDap - The new decimal alignment percentage
   */
  set decimalAlignPercent(newDap: number) {
    /* istanbul ignore else */
    if (newDap !== this._decimalAlignPercent) {
      this.lines.forEach((l) => (l.contentProps.decimalAlignPercent = newDap));
      this._decimalAlignPercent = newDap;
    }
  }

  private _styles: EditorV3Styles | undefined = this._defaultContentProps.styles;
  /**
   * Current available styles in the editor.
   */
  get styles() {
    return this._styles;
  }
  /**
   * Set available styles.
   *
   * @param newStyles - The new style definitions
   */
  set styles(newStyles: EditorV3Styles | undefined) {
    this._styles = newStyles;
  }
  /**
   * Get all styles currently used in the content.
   */
  get hasStyles() {
    const usedStyles = new Set<string>();
    this.lines.forEach((line) => {
      line.getAllStyles().forEach((s) => usedStyles.add(s));
    });
    return Array.from(usedStyles).sort((a, b) => a.localeCompare(b));
  }

  private _allowMarkdown = this._defaultContentProps.allowMarkdown;
  /**
   * Whether markdown syntax is allowed.
   */
  get allowMarkdown() {
    return this._allowMarkdown;
  }
  /**
   * Set whether markdown syntax is allowed.
   *
   * @param newAllow - Whether to allow markdown
   */
  set allowMarkdown(newAllow: boolean) {
    this._allowMarkdown = newAllow;
  }
  private _markdownSettings = this._defaultContentProps.markdownSettings;
  /**
   * Current markdown parsing settings.
   */
  get markdownSettings() {
    return this._markdownSettings;
  }
  /**
   * Set markdown parsing settings.
   *
   * @param newSettings - The new markdown settings
   */
  set markdownSettings(newSettings: IMarkdownSettings) {
    this._markdownSettings = newSettings;
    this.lines.forEach((l) => (l.contentProps.markdownSettings = newSettings));
  }
  private _showMarkdown = this._defaultContentProps.showMarkdown;
  /**
   * Whether to display markdown syntax instead of rendered content.
   */
  get showMarkdown() {
    return this._showMarkdown;
  }
  /**
   * Set whether to display markdown syntax.
   *
   * @param newShow - Whether to show markdown
   */
  set showMarkdown(newShow: boolean) {
    this._showMarkdown = newShow;
  }
  private _allowWindowView = this._defaultContentProps.allowWindowView;
  /**
   * Whether window view mode is allowed.
   */
  get allowWindowView() {
    return this._allowWindowView;
  }
  /**
   * Set whether window view mode is allowed.
   *
   * @param newAllow - Whether to allow window view
   */
  set allowWindowView(newAllow: boolean) {
    this._allowWindowView = newAllow;
  }
  private _atListFunction:
    | ((typedString: string) => Promise<EditorV3DropListItem<Record<string, string>>[]>)
    | undefined;
  /**
   * Function to fetch at-mention suggestions.
   */
  get atListFunction() {
    return this._atListFunction;
  }
  private _maxAtListLength = this._defaultContentProps.maxAtListLength;
  /**
   * Maximum number of at-mention suggestions to display.
   */
  get maxAtListLength() {
    return this._maxAtListLength;
  }

  /**
   * Current lines in the editor.
   */
  public lines: EditorV3Line[] = [];
  private _allowNewLine = this._defaultContentProps.allowNewLine;
  /**
   * Whether multiple lines are allowed.
   */
  get allowNewLine() {
    return this._allowNewLine;
  }
  /**
   * Set whether multiple lines are allowed.
   *
   * @param newAllow - Whether to allow new lines
   */
  set allowNewLine(newAllow: boolean) {
    this._allowNewLine = newAllow;
  }
  /**
   * Array of line lengths.
   */
  get lineLengths() {
    return this.lines.map((l) => l.lineLength);
  }
  /**
   * Array of word positions across all lines.
   */
  get wordPositions() {
    return this.lines.flatMap((l, ix) => l.wordPositions.map((w) => ({ ...w, line: ix })));
  }

  /**
   * Current caret position and lock status.
   */
  private _caretPosition: EditorV3PositionClass | null = null;
  /**
   * Gets the caret position.
   *
   * @returns The current caret position or null
   */
  get caretPosition(): EditorV3Position | null {
    return this._caretPosition?.pos ?? null;
  }
  /**
   * Sets the caret position.
   *
   * @param posInput - The new caret position or null
   */
  set caretPosition(posInput: EditorV3Position | null) {
    if (posInput === null) {
      this._caretPosition = null;
    } else if (this._caretPosition) {
      this._caretPosition.setCaret(posInput);
    } else {
      const newPos =
        posInput.focusAt === "start"
          ? new EditorV3PositionClass(
              posInput.endLine,
              posInput.endChar,
              posInput.startLine,
              posInput.startChar,
              this.lineLengths,
              this.wordPositions,
            )
          : new EditorV3PositionClass(
              posInput.startLine,
              posInput.startChar,
              posInput.endLine,
              posInput.endChar,
              this.lineLengths,
              this.wordPositions,
            );
      this._caretPosition = newPos;
    }
  }
  /**
   * Gets the caret position with focus information.
   *
   * @returns The current caret position with focus or null
   */
  get caretPositionF(): EditorV3PositionF | null {
    return this._caretPosition?.posF ?? null;
  }
  /**
   * Sets the caret position with focus information.
   *
   * @param posInput - The new caret position with focus or null
   */
  set caretPositionF(posInput: EditorV3PositionF | null) {
    if (posInput === null) {
      this._caretPosition = null;
    } else {
      this._caretPosition = new EditorV3PositionClass(
        posInput.initialLine,
        posInput.initialChar,
        posInput.focusLine,
        posInput.focusChar,
        this.lineLengths,
        this.wordPositions,
      );
    }
  }
  /**
   * Indicates if the current content can be updated with this selection.
   *
   * @param pos - The position to check
   * @returns True if caret is locked
   */
  public isCaretLocked(pos = this._caretPosition): boolean {
    /* istanbul ignore else */
    if (!pos || pos.isCollapsed) return false;
    const selectedBlocks = this.subLines(pos).flatMap((l) => l.textBlocks);
    return selectedBlocks.length > 1 && selectedBlocks.some((b) => b.isLocked);
  }
  /**
   * Check if any at blocks are present that can be locked.
   *
   * @returns True if lockable at blocks exist
   */
  private get _isLockable() {
    return this.lines.some((l) => l.textBlocks.some((tb) => tb.type === "at" && !tb.isLocked));
  }

  /**
   * Text from the element.
   *
   * @returns Plain text content
   */
  get text(): string {
    return this.lines.map((l) => l.lineText).join("\n");
  }

  /**
   * Markdown text from the element.
   *
   * @returns Markdown formatted content
   */
  get markdownText(): string {
    return this.lines.map((l) => l.lineMarkdown).join("\n");
  }

  /**
   * Returns true if the content is not plain text.
   *
   * @returns True if content has styles or special blocks
   */
  get isEnhanced(): boolean {
    return this.lines.some((l) => l.isEnhanced);
  }

  /**
   * Current content properties.
   *
   * @returns Content properties object
   */
  get contentProps(): EditorV3ContentProps {
    return {
      textAlignment: this._textAlignment,
      decimalAlignPercent: this._decimalAlignPercent,
      styles: this._styles,
      allowNewLine: this._allowNewLine,
      allowWindowView: this._allowWindowView,
      showMarkdown: this._showMarkdown,
      markdownSettings: this._markdownSettings,
      allowMarkdown: this._allowMarkdown,
      atListFunction: this.atListFunction,
      maxAtListLength: this._maxAtListLength,
    };
  }

  /**
   * Data representation of content.
   *
   * @returns Serializable content data
   */
  get data(): IEditorV3 {
    const contentProps = cloneDeep(this.contentProps);
    Object.keys(defaultContentProps).forEach((k) => {
      const key = k as keyof typeof defaultContentProps;
      /* istanbul ignore else */
      if (typeof this[key] === "function") {
        delete contentProps[key];
      }
      if (isEqual(this[key], this._defaultContentProps[key])) {
        delete contentProps[key];
      }
    });
    const lines = this.lines.map((l) => ({
      textBlocks: l.textBlocks.map((tb) => tb.data),
    }));
    const ret: IEditorV3 = { lines };
    if (Object.keys(contentProps).length > 0) {
      ret.contentProps = contentProps;
    }
    if (this._caretPosition) {
      ret.caretPosition = this._caretPosition.pos;
    }
    return ret;
  }

  /**
   * Element to render.
   *
   * @param renderProps - The rendering properties
   * @returns Document fragment containing the rendered HTML
   */
  public toHtml(renderProps: EditorV3RenderProps): DocumentFragment {
    // Remove any existing dropdowns
    const previousDropdown = document.querySelectorAll(".aiev3-dropdown-list");
    previousDropdown.forEach((dropdown) => dropdown.remove());
    // Create new DocumentFragment
    const ret = new DocumentFragment();
    // Add each line to the element
    this.lines.forEach((l) => ret.append(l.toHtml(renderProps)));
    // Add content properties node
    /* istanbul ignore else */
    if (renderProps.editableEl) renderProps.editableEl.append(ret);
    return ret;
  }

  /**
   * Convert the editor model into a markdown-styled HTML fragment.
   *
   * @param renderProps - Rendering options applied during build
   */
  public toMarkdownHtml(renderProps: EditorV3RenderProps): DocumentFragment {
    const ret = new DocumentFragment();
    // Add content to the element
    this.lines.forEach((l) => ret.append(l.toMarkdown(renderProps)));
    /* istanbul ignore else */
    if (renderProps.editableEl) renderProps.editableEl.append(ret);
    return ret;
  }

  /**
   * Create a new EditorV3Content instance from a string, DOM node or model.
   *
   * @param arg - Optional source content: HTML `div`, serialized JSON string or model
   * @param props - Optional content properties to override defaults
   */
  constructor(arg?: string | HTMLDivElement | IEditorV3, props?: EditorV3ContentPropsInput) {
    // Process incoming data
    /* istanbul ignore else */
    if (arg instanceof HTMLDivElement) {
      // Remove skip-read elements
      const skipRead = arg.querySelectorAll(".skip-read");
      skipRead.forEach((el) => el.remove());
      // Read in HTML
      const r = readV3Html(arg, props ?? this.contentProps);
      this._copyImport(r);
      this.caretPositionF = getCaretPosition(arg);
    } else if (typeof arg === "string") {
      // Check for stringified class input
      this.loadString(arg, props);
    }
    // Interface JSON input
    else if (arg !== undefined) {
      this._copyImport(arg, props);
    } else {
      this.lines = [new EditorV3Line()];
    }

    // Forced parameters
    if (props) {
      this._updateProps(props);
      this.lines.map((l) => (l.contentProps = this.contentProps));
    }
    if (this.lines.length === 0) {
      this.lines = [new EditorV3Line()];
    }
  }

  /**
   * Update internal content properties from provided settings.
   *
   * @param props - Partial content properties to merge into the instance
   */
  private _updateProps(props: EditorV3ContentPropsInput) {
    if (props.allowMarkdown !== undefined) this._allowMarkdown = props.allowMarkdown;
    if (props.allowNewLine !== undefined) this._allowNewLine = props.allowNewLine;
    if (props.allowWindowView !== undefined) this._allowWindowView = props.allowWindowView;
    if (props.decimalAlignPercent !== undefined)
      this._decimalAlignPercent = props.decimalAlignPercent;
    if (props.markdownSettings !== undefined) this._markdownSettings = props.markdownSettings;
    if (props.showMarkdown !== undefined) this._showMarkdown = props.showMarkdown;
    if (props.styles !== undefined) this._styles = props.styles;
    if (props.textAlignment !== undefined) this._textAlignment = props.textAlignment;
    if (props.atListFunction !== undefined) this._atListFunction = props.atListFunction;
    if (props.maxAtListLength !== undefined) this._maxAtListLength = props.maxAtListLength;
  }

  /* istanbul ignore else */
  /**
   * Debug helper that logs a representation of the content lines to the console.
   *
   * @param from - Label to prefix the debug output
   */
  private _debugText(from: string): void {
    console.debug(
      from + "\n",
      this.lines.map((l) => l.textBlocks.map((tb) => tb.text).join("|")).join("\n"),
    );
  }

  /**
   * Copy data from a parsed model into this instance, merging content props.
   *
   * @param read - Model data to import into this instance
   * @param props - Optional properties to merge during import
   */
  private _copyImport(read: IEditorV3, props?: EditorV3ContentPropsInput): void {
    const consolidatedProps = { ...read.contentProps, ...props };
    this._updateProps(consolidatedProps);
    this.lines = read.lines.map((l) =>
      l instanceof EditorV3Line ? l : new EditorV3Line(l, consolidatedProps),
    );
    this.caretPosition = read.caretPosition ?? null;
  }

  /**
   * Load content from a serialized string representation. Attempts JSON first,
   * falls back to parsing HTML and finally to a V3 string conversion.
   *
   * @param arg - String input containing JSON, HTML or plain text
   * @param props - Optional content props to apply during load
   */
  public loadString(arg: string, props?: EditorV3ContentPropsInput) {
    try {
      // Check for stringified class input
      const jsonInput: IEditorV3 = JSON.parse(arg);
      if (!Array.isArray(jsonInput.lines)) throw "No lines";
      this._copyImport(jsonInput, props);
    } catch {
      // Check if the input is HTML
      /* istanbul ignore else */
      if (arg.match(/^<div(.*)<\/div>$/i)) {
        // Read in v3 HTML/text
        const div = document.createElement("div");
        div.innerHTML = arg;
        // Check this is a v3 input
        /* istanbul ignore else */
        if (
          Array.from(div.childNodes).every(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              node.nodeName === "DIV" &&
              ((node as HTMLDivElement).classList.contains("aiev3-line") ||
                (node as HTMLDivElement).classList.contains("aiev3-markdown-line")),
          )
        ) {
          const r = readV3Html(div, props ?? this.contentProps);
          this._copyImport(r);
          return;
        }
      }
      this._copyImport(stringToV3(arg));
    }
  }

  /**
   * Return a subset of `EditorV3Line` instances from the content based on a position.
   *
   * @param pos - Position object describing a range within the content
   */
  public subLines(pos: EditorV3Position): EditorV3Line[] {
    const ret: EditorV3Line[] = [];
    if (
      pos.startLine === pos.endLine &&
      pos.startChar === pos.endChar &&
      pos.startLine < this.lines.length
    ) {
      const style = this.lines[pos.startLine].getStyleAt(pos.startChar);
      ret.push(new EditorV3Line([textBlockFactory({ text: "", style })], this.contentProps));
    }
    // Check selection contains something
    if (
      pos.startLine < this.lines.length &&
      ((pos.startLine === pos.endLine && pos.startChar < pos.endChar) ||
        pos.startLine < pos.endLine)
    ) {
      // First line
      /* istanbul ignore else */
      if (pos.startLine <= pos.endLine) {
        const subBlocks = new EditorV3Line(
          this.lines[pos.startLine].subBlocks(
            pos.startChar,
            pos.startLine === pos.endLine ? pos.endChar : undefined,
          ),
          this.contentProps,
        );
        if (subBlocks.textBlocks.length === 0) {
          subBlocks.textBlocks.push(textBlockFactory({ text: "" }));
        }
        ret.push(subBlocks);
      }
      // At least one line between
      if (pos.startLine + 1 < pos.endLine) {
        ret.push(...this.lines.slice(pos.startLine + 1, pos.endLine));
      }
      // End line to end character
      if (pos.startLine < pos.endLine && pos.endLine < this.lines.length) {
        ret.push(new EditorV3Line(this.lines[pos.endLine].upToPos(pos.endChar), this.contentProps));
      }
    }
    return ret;
  }

  /**
   * Return the lines from the start of the content up to the specified
   * end position. The returned array represents the content slice ending
   * at the given line/character (exclusive of any subsequent content).
   *
   * @param endLine - Zero-based index of the line to end at.
   * @param endChar - Character index on `endLine` to end at.
   * @returns An array of `EditorV3Line` representing the content up to the
   *          specified position.
   */
  public upToPos(endLine: number, endChar: number): EditorV3Line[] {
    return this.subLines({ startLine: 0, startChar: 0, endLine, endChar });
  }

  /**
   * Return the lines from the specified start position to the end of the
   * content. This is the complement of `upToPos` and includes all content
   * from `startLine`/`startChar` through the last line.
   *
   * @param startLine - Zero-based index of the line to start from.
   * @param startChar - Character index on `startLine` to start from.
   * @returns An array of `EditorV3Line` representing the content from the
   *          specified position to the end.
   */
  public fromPos(startLine: number, startChar: number): EditorV3Line[] {
    return this.subLines({
      startLine,
      startChar,
      endLine: this.lines.length - 1,
      endChar: this.lines[this.lines.length - 1].lineLength,
    });
  }

  /**
   * Get the active style at a particular line and character index.
   * Returns the style name if present, otherwise `undefined`.
   *
   * @param line - Zero-based line index to query.
   * @param character - Character index within the line to query.
   * @returns The style name at the specified position, or `undefined`.
   */
  public getStyleAt(line: number, character: number): string | undefined {
    return line < this.lines.length
      ? this.lines[line].getStyleAt(character)
      : // istanbul ignore next
        undefined;
  }

  /**
   * Split the content at the given position. The line containing `pos`
   * will be split into two lines and the internal `lines` array updated.
   * The returned value is the new collapsed caret position after the split.
   *
   * @param pos - Position at which to split the line.
   * @returns A collapsed `EditorV3Position` representing the caret after
   *          the split.
   */
  public splitLine(pos: EditorV3Position): EditorV3Position {
    const u = this.upToPos(pos.startLine, pos.startChar);
    const f = this.fromPos(pos.endLine, pos.endChar);
    const newLines = [...u, ...f];
    this.lines = newLines;
    return {
      isCollapsed: true,
      startLine: pos.startLine + 1,
      startChar: 0,
      endLine: pos.startLine + 1,
      endChar: 0,
    };
  }

  /**
   * Merge the specified line with the following line if the following
   * line exists. After merging, the following line is removed.
   *
   * @param line - Index of the line to merge with the next line.
   */
  public mergeLines(line: number) {
    if (this.lines.length > line + 1) {
      this.lines[line].insertBlocks(this.lines[line + 1].textBlocks, this.lines[line].lineLength);
      this.lines.splice(line + 1, 1);
    }
  }

  /**
   * Delete a character at or around the current caret position. When
   * `backwards` is true the method behaves like a backspace; otherwise
   * it behaves like a forward delete. Optionally provide a `pos` to set
   * the caret before deletion.
   *
   * @param backwards - If true, delete backwards (backspace). If false,
   *        delete forwards (delete).
   * @param pos - Optional position to set as the caret before deletion.
   */
  public deleteCharacter(backwards: boolean, pos?: EditorV3Position) {
    // Update caret position
    if (pos) {
      this.caretPosition = pos;
    }
    // Check caret position exists
    if (this._caretPosition && this._caretPosition.isCollapsed) {
      // Extend selection one place backwards
      if (backwards) this._caretPosition?.moveLeft(true, false);
      // Extend selection one place forwards
      else this._caretPosition?.moveRight(true, false);
    }
    /* istanbul ignore else */
    if (this._caretPosition) {
      this.splice(this._caretPosition.pos);
    }
  }

  /**
   * Find all occurrences of the given text in the content and return an
   * array of `EditorV3Position` ranges for each match. Returns `null` if
   * no matches are found.
   *
   * @param text - The substring to search for.
   * @returns An array of positions for each match, or `null` when none are
   *          found.
   */
  public getTextPosition(text: string): EditorV3Position[] | null {
    const ret: EditorV3Position[] = [];
    this.lines.forEach((l, ix) => {
      l.textBlocks.forEach((tb) => {
        let pos = 0;
        while (tb.text.indexOf(text, pos) >= 0) {
          const found = tb.text.indexOf(text, pos);
          /* istanbul ignore else */
          if (found >= 0) {
            ret.push({
              isCollapsed: false,
              startLine: ix,
              startChar: tb.lineStartPosition + found,
              endLine: ix,
              endChar: tb.lineStartPosition + found + text.length,
            });
            pos = found + text.length;
          }
        }
      });
    });
    return ret.length > 0 ? ret : null;
  }

  /**
   * Replace the content in `pos` with the optional `newLines`. Returns
   * the lines that were removed by the splice operation.
   *
   * @param pos - The position range to replace.
   * @param newLines - Optional array of `EditorV3Line` to insert at `pos`.
   * @returns An array of `EditorV3Line` representing the removed content.
   */
  public splice(pos: EditorV3Position, newLines?: EditorV3Line[]): EditorV3Line[] {
    // if (this._showMarkdown) {
    //   return this.spliceMarkdown(pos, newLines);
    // } else
    if (
      pos.startLine < this.lines.length &&
      pos.startLine <= pos.endLine &&
      !(pos.startLine === pos.endLine && pos.endChar < pos.startChar)
    ) {
      const ret: EditorV3Line[] = this.subLines(pos);
      const u = this.upToPos(pos.startLine, pos.startChar);
      const f = this.fromPos(pos.endLine, pos.endChar);
      this.lines = [
        ...u,
        ...(newLines ? newLines.map((l) => new EditorV3Line(l, this.contentProps)) : []),
        ...f,
      ];
      // Merge line after U
      this.mergeLines(pos.startLine);
      // Merge line after newLines if it exists and there is something after it
      if (newLines?.length) this.mergeLines(pos.startLine + newLines.length - 1);
      // Check all lines have at least 1 text block
      this.lines.forEach((l) => {
        /* istanbul ignore else */
        if (l.textBlocks.length === 0) {
          l.textBlocks.push(textBlockFactory({ text: "", style: l.getStyleAt(0) }));
        }
      });
      // Calculate end character position
      const endChar =
        !newLines || newLines.length === 0
          ? pos.startChar
          : newLines?.length === 1
            ? pos.startChar + newLines[0].lineLength
            : newLines[newLines.length - 1].lineLength;
      // Update caret position if it is set
      if (this._caretPosition) {
        this._caretPosition = new EditorV3PositionClass(
          pos.startLine + (newLines ? newLines.length - 1 : 0),
          endChar,
          pos.startLine + (newLines ? newLines?.length - 1 : 0),
          endChar,
          this.lineLengths,
          this.wordPositions,
        );
      }
      return ret;
    }
    return [];
  }

  /**
   * Apply a named style to the text within the given position or the
   * current selection. When `applyPos` is omitted the current caret
   * selection is used. The method returns `this` to allow chaining.
   *
   * @param styleName - The style identifier to apply.
   * @param applyPos - Optional position to apply the style to instead of
   *        using the current selection.
   * @returns The current `EditorV3Content` instance for chaining.
   */
  public applyStyle(styleName: string, applyPos?: EditorV3Position) {
    /* istanbul ignore else */
    if (applyPos || (this._caretPosition && !this._caretPosition.isCollapsed)) {
      const pos = applyPos ?? (this._caretPosition?.pos as EditorV3Position);
      /* istanbul ignore else */
      if (pos.startLine < this.lines.length && pos.startLine <= pos.endLine) {
        for (let _i = pos.startLine; _i <= pos.endLine && _i < this.lines.length; _i++) {
          this.lines[_i].applyStyle(
            styleName,
            _i === pos.startLine ? pos.startChar : 0,
            _i === pos.endLine ? pos.endChar : Infinity,
          );
        }
        this.caretPosition = {
          startLine: pos.endLine,
          startChar: pos.endChar,
          endLine: pos.endLine,
          endChar: pos.endChar,
        };
      }
    }
    return this;
  }

  /**
   * Remove any applied style from the text within `removePos` or the
   * current selection. Returns `this` for chaining.
   *
   * @param removePos - Optional position to remove styles from. If omitted
   *        the current selection is used.
   * @returns The current `EditorV3Content` instance.
   */
  public removeStyle(removePos?: EditorV3Position) {
    /* istanbul ignore else */
    if (removePos || (this._caretPosition && !this._caretPosition.isCollapsed)) {
      const pos = removePos ?? (this._caretPosition?.pos as EditorV3Position);
      /* istanbul ignore else */
      if (pos.startLine < this.lines.length && pos.startLine <= pos.endLine) {
        for (let _i = pos.startLine; _i <= pos.endLine && _i < this.lines.length; _i++) {
          this.lines[_i].removeStyle(
            _i === pos.startLine
              ? pos.startChar
              : // istanbul ignore next
                0,
            _i === pos.endLine
              ? pos.endChar
              : // istanbul ignore next
                Infinity,
          );
        }
        this.caretPosition = {
          startLine: pos.endLine,
          startChar: pos.endChar,
          endLine: pos.endLine,
          endChar: pos.endChar,
        };
      }
    }
    return this;
  }

  /**
   * Remove styles from the content that match the specified style names.
   *
   * @param styleNames - Array of style names to remove
   */
  public cleanStyles(styleNames: string[]) {
    this.lines.forEach((l) => l.cleanStyles(styleNames));
  }

  /**
   * Delete text from the content that uses the specified styles.
   *
   * @param styleNames - Array of style names to delete
   */
  public deleteStyles(styleNames: string[]) {
    const lengthsBefore = this.lineLengths;
    this.lines.forEach((l) => l.deleteStyles(styleNames));
    // Delete whole lines if they are now empty
    if (this.lines.length > 1) {
      this.lines = this.lines.filter((l, ix) => l.lineLength !== 0 || lengthsBefore[ix] === 0);
    }
  }

  /**
   * Redraw the editor content into the provided editable element. This
   * will render the content as HTML (or markdown) and restore the caret
   * position if it is set.
   *
   * @param editableEl - The `HTMLDivElement` to render the editor content
   *        into.
   */
  public redraw(editableEl: HTMLDivElement) {
    // Set active block
    /* istanbul ignore else */
    if (this._caretPosition)
      this.lines[this._caretPosition.startLine].setActiveBlock(this._caretPosition);
    // Draw HTML
    editableEl.innerHTML = "";
    if (this._showMarkdown) {
      this.toMarkdownHtml({ editableEl, markdownSettings: this._markdownSettings });
    } else {
      this.toHtml({
        editableEl,
        atListFunction: this._atListFunction,
        maxAtListLength: this._maxAtListLength,
        caretPosition: this._caretPosition,
        styles: this._styles,
      });
    }
    // Set caret position
    /* istanbul ignore else */
    if (this._caretPosition) {
      setCaretPosition(editableEl, this._caretPosition);
    }
  }

  /**
   * Validate and enforce the editor's current status: update the active
   * block based on the caret and enforce locked-block selection rules.
   */
  public checkStatus() {
    // Set active block
    const activeBlock =
      this._caretPosition &&
      this._caretPosition.endChar < this.lines[this._caretPosition.endLine].lineLength &&
      this.lines[this._caretPosition.focusLine].setActiveBlock(this._caretPosition);
    // If active block is locked select all
    /* istanbul ignore else */
    if (activeBlock && activeBlock.isLocked && this._caretPosition) {
      this.caretPosition = {
        startLine: this._caretPosition.focusLine,
        startChar: activeBlock.lineStartPosition,
        endLine: this._caretPosition.focusLine,
        endChar: activeBlock.lineEndPosition,
      };
    }
  }

  /**
   * Handle keyboard down events for editor interactions including
   * selection, navigation, deletion and special key behaviours.
   *
   * @param e - The keyboard event from the editable element.
   */
  public handleKeydown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Select all
    /* istanbul ignore else */
    if (e.ctrlKey && e.code === "KeyA" && this.text !== "") {
      e.stopPropagation();
      e.preventDefault();
      this.caretPosition = {
        startLine: 0,
        startChar: 0,
        endLine: this.lines.length - 1,
        endChar: this.lines[this.lines.length - 1].lineLength,
      };
    }
    // Cursor movement
    else if (
      [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Backspace",
        "Delete",
        "Escape",
      ].includes(e.key)
    ) {
      e.stopPropagation();
      e.preventDefault();
      if (this._caretPosition === null) {
        this.caretPosition = { startLine: 0, startChar: 0, endLine: 0, endChar: 0 };
      }
      switch (e.key) {
        case "ArrowLeft":
          this._caretPosition!.moveLeft(e.shiftKey, e.ctrlKey);
          break;
        case "ArrowRight":
          this._caretPosition!.moveRight(e.shiftKey, e.ctrlKey);
          break;
        case "ArrowUp":
          this._caretPosition!.moveUp(e.shiftKey, e.ctrlKey);
          break;
        case "ArrowDown":
          this._caretPosition!.moveDown(e.shiftKey, e.ctrlKey);
          break;
        case "Home":
          this._caretPosition!.moveHome(e.shiftKey, e.ctrlKey);
          break;
        case "End":
          this._caretPosition!.moveEnd(e.shiftKey, e.ctrlKey);
          break;
        case "Backspace":
          this.deleteCharacter(true);
          break;
        case "Delete":
          this.deleteCharacter(false);
          break;
        case "Escape":
          /* istanbul ignore else */
          if (this._isLockable) {
            const lockBlock = this.lines[this._caretPosition!.startLine].setActiveBlock(
              this._caretPosition!,
            );
            /* istanbul ignore else */
            if (lockBlock && lockBlock.type === "at") {
              lockBlock.setActive(false);
              lockBlock.isLocked = true;
            }
          }
      }
    }
    // Enter key cannot be used here
    else if (this._caretPosition && this.allowNewLine && e.key === "Enter") {
      e.stopPropagation();
      e.preventDefault();
      this.caretPosition = this.splitLine(this._caretPosition);
    } else if (this._caretPosition) {
      this._caretPosition.lastAction = "keydown";
      // Reset a locked block to no style if it is the active block
      const activeBlock = this.lines[this._caretPosition.startLine].getBlockAt(
        this._caretPosition.startChar,
      );
      /* istanbul ignore else */
      if (
        activeBlock &&
        (activeBlock ===
          this.lines[this._caretPosition.endLine].getBlockAt(this._caretPosition.endChar),
        activeBlock.isLocked && !this._caretPosition.isCollapsed)
      ) {
        activeBlock.style = undefined;
      }
    }
  }

  /**
   * Handle keyboard up events. This updates the caret-derived position
   * cache and suppresses default behaviour for keys managed by the
   * editor.
   *
   * @param e - The keyboard event from the editable element.
   */
  public handleKeyup(e: React.KeyboardEvent<HTMLDivElement>) {
    // Update caret position
    /* istanbul ignore else */
    if (this._caretPosition) {
      this.caretPositionF = getCaretPosition(e.currentTarget);
    }
    // Suppress returns on movement keys
    /* istanbul ignore else */
    if (
      [
        "Enter",
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        // "Escape", Escape is ok - need to trigger the usual behaviour
      ].includes(e.code) ||
      (e.ctrlKey && e.code === "KeyA")
    ) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handle copy and cut inside the holding div
   *
   * @param e Copy or cut event
   */
  public handleCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    /* istanbul ignore else */
    if (this._caretPosition) {
      const toClipboard = this.subLines(this._caretPosition);
      e.clipboardData.setData("text/plain", toClipboard.map((l) => l.lineText).join("\n"));
      e.clipboardData.setData("text/html", toClipboard.map((l) => l.toHtml({}).outerHTML).join(""));
      e.clipboardData.setData("data/aiev3", JSON.stringify(toClipboard.map((l) => l.data)));
      /* istanbul ignore else */
      if (e.type === "cut" && !this.isCaretLocked()) {
        this.splice(this._caretPosition);
      }
    }
  };

  /**
   * Handle paste events into the editor. This supports pastes from the
   * editor's custom clipboard format (`data/aiev3`) as well as plain text
   * and inserts the resulting lines at the current caret position.
   *
   * @param e - The clipboard event generated by the paste action.
   */
  public handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    // Check cursor is present, and is does not contain a locked block
    /* istanbul ignore else */
    if (this._caretPosition && !this.isCaretLocked()) {
      // Get clipboard data
      const lines: EditorV3Line[] = [];
      const linesImport = (
        e.clipboardData.getData("data/aiev3") !== ""
          ? JSON.parse(e.clipboardData.getData("data/aiev3"))
          : e.clipboardData
              .getData("text/plain")
              .split("\n")
              .map((t) => ({
                textBlocks: [{ text: t }],
              }))
      ) as IEditorV3Line[];
      // Just text blocks if only one line is allowed
      /* istanbul ignore else */
      if (linesImport.length > 1 && !this.allowNewLine) {
        const textBlocks: EditorV3BlockClass[] = linesImport
          .flatMap((l) => l.textBlocks)
          .map((tb) => textBlockFactory(tb));
        lines.push(new EditorV3Line(textBlocks, this.contentProps));
      } else {
        lines.push(...linesImport.map((l) => new EditorV3Line(l, this.contentProps)));
      }
      // Splice in new data and set new content
      this.splice(this._caretPosition, lines);
    }
  }
}
